import { Component, Output, EventEmitter, OnInit, signal, inject, input, DestroyRef } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { TrackingMode, Visibility } from '@vendasta/email';
import { EmailLabelingConfigService } from './email-labeling-config.service';
import {
  DEFAULT_LABELS,
  PREDEFINED_COLORS,
  MAX_LABEL_NAME_LENGTH,
  MAX_LABEL_DESCRIPTION_LENGTH,
  MAX_CONFIG_NAME_LENGTH,
  MAX_LABELS,
} from './constants';
import { LabelFormData, ColorOption, SaveConfigData, MessageCategoryConfig } from './models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';

@Component({
  selector: 'email-labeling-config',
  standalone: true,
  imports: [
    TranslateModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    GalaxyFormFieldModule,
  ],
  templateUrl: './email-labeling-config.component.html',
  styleUrls: ['./email-labeling-config.component.scss'],
})
export class EmailLabelingConfigComponent implements OnInit {
  readonly namespace = input.required<string>();
  readonly userId = input.required<string>();
  readonly configId = input<string | null>(null);
  readonly existingConfig = input<MessageCategoryConfig | null>(null);

  @Output() saved = new EventEmitter<SaveConfigData>();
  @Output() cancelled = new EventEmitter<void>();

  protected readonly labels = signal<LabelFormData[]>([]);
  protected readonly configurationName = signal<string>('');
  protected readonly colors: ColorOption[] = [...PREDEFINED_COLORS];
  protected readonly maxNameLength = MAX_LABEL_NAME_LENGTH;
  protected readonly maxDescriptionLength = MAX_LABEL_DESCRIPTION_LENGTH;
  protected readonly maxConfigNameLength = MAX_CONFIG_NAME_LENGTH;
  protected readonly maxLabels = MAX_LABELS;
  protected readonly isEditMode = signal<boolean>(false);
  protected readonly trackingMode = signal<number>(TrackingMode.TRACKING_MODE_CONTACTS_ONLY);
  protected readonly defaultInboundVisibility = signal<number>(Visibility.VISIBILITY_PRIVATE);

  protected readonly trackingModeOptions = [
    {
      value: TrackingMode.TRACKING_MODE_CONTACTS_ONLY,
      labelKey: 'ADMINISTRATION.EMAIL_LABELING_SETTINGS.TRACKING_MODE_CONTACTS_ONLY',
    },
    {
      value: TrackingMode.TRACKING_MODE_ALL_SENDERS,
      labelKey: 'ADMINISTRATION.EMAIL_LABELING_SETTINGS.TRACKING_MODE_ALL_SENDERS',
    },
  ];

  protected readonly visibilityOptions = [
    { value: Visibility.VISIBILITY_PRIVATE, labelKey: 'ADMINISTRATION.EMAIL_LABELING_SETTINGS.VISIBILITY_PRIVATE' },
    { value: Visibility.VISIBILITY_INTERNAL, labelKey: 'ADMINISTRATION.EMAIL_LABELING_SETTINGS.VISIBILITY_INTERNAL' },
  ];

  private readonly service = inject(EmailLabelingConfigService);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    const existingConfig = this.existingConfig();
    const configId = this.configId();

    if (!existingConfig && configId && configId !== 'new') {
      this.isEditMode.set(true);
      if (this.namespace()) {
        this.loadExistingConfig(configId);
      }
    } else if (existingConfig) {
      this.isEditMode.set(true);
      this.loadConfigData(existingConfig);
    } else {
      this.initializeLabels();
    }
  }

  private loadConfigData(config: MessageCategoryConfig): void {
    this.configurationName.set(config.name);
    if (config.trackingMode !== undefined) {
      this.trackingMode.set(config.trackingMode);
    }
    if (config.defaultInboundVisibility !== undefined) {
      this.defaultInboundVisibility.set(config.defaultInboundVisibility);
    }
    const formattedLabels = config.categories.map((cat) => ({
      position: cat.position,
      name: cat.name,
      colorHex: cat.color.toUpperCase(),
      description: cat.description,
    }));
    this.labels.set(formattedLabels);
  }

  protected loadExistingConfig(configId: string): void {
    if (!this.namespace()) {
      return;
    }

    this.service
      .getConfigById(this.namespace(), configId)
      .pipe(
        tap((config) => (config ? this.loadConfigData(config) : this.initializeLabels())),
        catchError(() => {
          this.initializeLabels();
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  protected initializeLabels(): void {
    const initialLabels = DEFAULT_LABELS.map((label) => ({
      position: label.position,
      name: label.name,
      colorHex: label.colorHex,
      description: label.description,
    }));
    this.labels.set(initialLabels);
  }

  protected onColorChange(position: number, colorHex: string): void {
    const updatedLabels = this.labels().map((label) => (label.position === position ? { ...label, colorHex } : label));
    this.labels.set(updatedLabels);
  }

  protected onNameChange(position: number, name: string): void {
    const updatedLabels = this.labels().map((label) => (label.position === position ? { ...label, name } : label));
    this.labels.set(updatedLabels);
  }

  protected onDescriptionChange(position: number, description: string): void {
    const updatedLabels = this.labels().map((label) =>
      label.position === position ? { ...label, description } : label,
    );
    this.labels.set(updatedLabels);
  }

  protected onConfigNameChange(name: string): void {
    this.configurationName.set(name);
  }

  protected onSave(): void {
    const validLabels = this.labels().filter((label) => label.name.trim().length > 0);
    this.saved.emit({
      configName: this.configurationName(),
      labels: validLabels,
      trackingMode: this.trackingMode(),
      defaultInboundVisibility: this.defaultInboundVisibility(),
    });
  }

  protected onCancel(): void {
    this.cancelled.emit();
  }

  protected isFormValid(): boolean {
    const hasValidConfigName = this.configurationName().trim().length > 0;
    const hasAtLeastOneLabel = this.labels().some((label) => label.name.trim().length > 0);
    return hasValidConfigName && hasAtLeastOneLabel;
  }

  protected addLabel(): void {
    const currentLabels = this.labels();
    const nextPosition = currentLabels.length > 0 ? Math.max(...currentLabels.map((l) => l.position)) + 1 : 1;
    const newLabel: LabelFormData = {
      position: nextPosition,
      name: '',
      colorHex: PREDEFINED_COLORS[0].hex,
      description: '',
    };
    this.labels.set([...currentLabels, newLabel]);
  }

  protected removeLabel(position: number): void {
    const updatedLabels = this.labels().filter((label) => label.position !== position);
    // Reindex positions to be sequential
    const reindexedLabels = updatedLabels.map((label, index) => ({
      ...label,
      position: index + 1,
    }));
    this.labels.set(reindexedLabels);
  }
}
